import React from 'react';
import { Check, Shield, X } from 'lucide-react';

const FEATURES = [
  {
    name: 'Connect Your Own Preferred LLMs via API',
    subtitle: 'Totally BYO-Key. Supports Claude, OpenAI, Gemini, Mistral, Grok, OpenRouter, and more.',
    local: true,
    cloud: true,
  },
  {
    name: 'Dedicated Private Workspaces',
    local: '3 local',
    cloud: 'Unlimited',
  },
  {
    name: 'Workspace file browser, docs view, code view, and to-dos, plus global snippets',
    local: true,
    cloud: true,
  },
  {
    name: 'Built-in tools like web search, context pruning, and message rewind',
    local: true,
    cloud: true,
  },
  {
    name: 'Support for third-party plug-and-play MCP servers',
    local: true,
    cloud: true,
  },
  {
    name: 'Workspace history and memory',
    local: 'On this device',
    cloud: 'Synced everywhere',
  },
  {
    name: 'Encrypted backup and cross-device continuity',
    local: false,
    cloud: true,
  },
  {
    name: 'Agent To-Do List & Autonomous Cloud Task Delegation',
    subtitle: 'Agents that run tasks sequentially from to-do lists you provide. Tasks are executed in the cloud and returned to your workspace.',
    local: false,
    cloud: true,
  },
  {
    name: 'Assistant Email & Remote Skills',
    local: false,
    cloud: true,
  },
  {
    name: 'Bring Your Own Local Model',
    subtitle: 'Connect Ollama, LM Studio, or any private OpenAI-compatible endpoint you host yourself.',
    local: false,
    cloud: true,
  },
];

const VARIANT_STYLES = {
  landing: {
    shell: 'rounded-xl border border-white/10 bg-black/20 p-6 backdrop-blur-sm',
    frame: 'overflow-hidden rounded-lg border border-white/10',
    header: 'grid grid-cols-5 border-b border-white/10',
    featureHeading: 'col-span-3 p-4 font-medium text-white/80',
    localHeader: 'col-span-1 border-x border-white/10 p-4 text-center',
    cloudHeader: 'col-span-1 p-4 text-center',
    localTitle: 'text-lg font-bold text-orange-400',
    cloudTitle: 'text-lg font-bold text-orange-400',
    localPrice: 'mt-1 text-sm text-white',
    cloudPrice: 'mt-1 text-sm text-white',
    row: 'grid grid-cols-5 border-b border-white/10 last:border-b-0',
    featureCell: 'col-span-3 flex flex-col justify-center p-4 text-sm text-white/80',
    icon: 'text-orange-400',
    mutedIcon: 'text-white/40',
    subtitle: 'ml-6 mt-1 text-[11px] italic text-white/50',
    localCell: 'col-span-1 flex items-center justify-center border-x border-white/10 p-4',
    cloudCell: 'col-span-1 flex items-center justify-center p-4',
    textValue: 'text-center text-sm text-white/80',
    check: 'h-5 w-5 text-green-500',
    x: 'h-5 w-5 text-white/30',
    ctaRow: 'grid grid-cols-5 gap-4 p-4',
    ctaCopy: 'col-span-3 flex items-center text-sm text-white/60',
    button: 'flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-orange-500 to-amber-500 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition-all duration-300 hover:from-orange-600 hover:to-amber-600 disabled:opacity-50 disabled:cursor-not-allowed',
    footer: 'mt-4 text-center text-sm text-white/60',
  },
  settings: {
    shell: 'rounded-xl border border-border-color bg-surface-raised p-4 sm:p-6',
    frame: 'overflow-hidden rounded-lg border border-border-color bg-surface',
    header: 'grid grid-cols-5 border-b border-border-color',
    featureHeading: 'col-span-3 p-4 font-medium text-on-surface/80',
    localHeader: 'col-span-1 border-x border-border-color p-4 text-center',
    cloudHeader: 'col-span-1 p-4 text-center',
    localTitle: 'text-lg font-bold text-primary-light',
    cloudTitle: 'text-lg font-bold text-primary-light',
    localPrice: 'mt-1 text-sm text-on-surface',
    cloudPrice: 'mt-1 text-sm text-on-surface',
    row: 'grid grid-cols-5 border-b border-border-color last:border-b-0',
    featureCell: 'col-span-3 flex flex-col justify-center p-4 text-sm text-on-surface/80',
    icon: 'text-primary-light',
    mutedIcon: 'text-on-surface-dim',
    subtitle: 'ml-6 mt-1 text-[11px] italic text-on-surface-dim',
    localCell: 'col-span-1 flex items-center justify-center border-x border-border-color p-4',
    cloudCell: 'col-span-1 flex items-center justify-center p-4',
    textValue: 'text-center text-sm text-on-surface/80',
    check: 'h-5 w-5 text-success',
    x: 'h-5 w-5 text-on-surface-dim',
    ctaRow: 'grid grid-cols-5 gap-4 p-4',
    ctaCopy: 'col-span-3 flex items-center text-sm text-on-surface-dim',
    button: 'flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed',
    footer: 'mt-4 text-center text-sm text-on-surface-dim',
  },
};

export function WorkspacePricingChart({
  variant = 'landing',
  onLocalAction,
  onCloudAction,
  localActionLabel = 'Start Free',
  cloudActionLabel = 'Get Pro',
  localActionDisabled = false,
  cloudActionDisabled = false,
  summaryText = 'Start free on one device. Upgrade when you want secure sync and continuity everywhere.',
  footerText = 'Bring Your Own Key (BYOK) to every plan. Privacy in your control. Fully Encrypted Chats & Storage.',
}) {
  const styles = VARIANT_STYLES[variant] || VARIANT_STYLES.landing;

  return (
    <div className={styles.shell}>
      <div className={styles.frame}>
        <div className={styles.header}>
          <div className={styles.featureHeading}>Features</div>
          <div className={styles.localHeader}>
            <div className={styles.localTitle}>Local</div>
            <div className={styles.localPrice}>Free</div>
          </div>
          <div className={styles.cloudHeader}>
            <div className={styles.cloudTitle}>Cloud-Synced</div>
            <div className={styles.cloudPrice}>
              $9<span className="opacity-60">/mo</span>
            </div>
          </div>
        </div>

        {FEATURES.map((feature) => (
          <div key={feature.name} className={styles.row}>
            <div className={styles.featureCell}>
              <div className="flex items-center">
                <Shield className={`mr-2 h-4 w-4 flex-shrink-0 ${styles.icon}`} />
                <span>{feature.name}</span>
              </div>
              {feature.subtitle && <div className={styles.subtitle}>{feature.subtitle}</div>}
            </div>

            <div className={styles.localCell}>
              {typeof feature.local === 'string' ? (
                <span className={styles.textValue}>{feature.local}</span>
              ) : feature.local ? (
                <Check className={styles.check} />
              ) : (
                <X className={styles.x} />
              )}
            </div>

            <div className={styles.cloudCell}>
              {typeof feature.cloud === 'string' ? (
                <span className={styles.textValue}>{feature.cloud}</span>
              ) : feature.cloud ? (
                <Check className={styles.check} />
              ) : (
                <X className={styles.x} />
              )}
            </div>
          </div>
        ))}

        <div className={styles.ctaRow}>
          <div className={styles.ctaCopy}>{summaryText}</div>
          <div className="col-span-1">
            <button
              type="button"
              onClick={onLocalAction}
              disabled={localActionDisabled || !onLocalAction}
              className={styles.button}
            >
              {localActionLabel}
            </button>
          </div>
          <div className="col-span-1">
            <button
              type="button"
              onClick={onCloudAction}
              disabled={cloudActionDisabled || !onCloudAction}
              className={styles.button}
            >
              {cloudActionLabel}
            </button>
          </div>
        </div>
      </div>

      <p className={styles.footer}>{footerText}</p>
    </div>
  );
}

export default WorkspacePricingChart;